import { gql, useMutation } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import alIndiaLogo from 'media/alIndiaLogo.png';

import debounce from 'utils/debounce';

const RESOLVE_TOKEN = gql`
  mutation ResolvePortfolioUpdateToken($token: String!) {
    resolvePortfolioUpdateToken(token: $token) {
      requestId
      dealId
      companyName
      formSlug
      savedFields
    }
  }
`;

const SAVE_PROGRESS = gql`
  mutation SavePortfolioUpdateProgress($token: String!, $fields: String!) {
    savePortfolioUpdateProgress(token: $token, fields: $fields)
  }
`;

const COMPLETE_UPDATE = gql`
  mutation CompletePortfolioUpdateViaOpnForm($token: String!, $submittedFields: String!) {
    completePortfolioUpdateViaOpnForm(token: $token, submittedFields: $submittedFields) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ResolvedData {
  requestId: string;
  dealId: string;
  companyName: string;
  formSlug: string;
  savedFields: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PortfolioUpdatePublic() {
  const { token } = useParams<{ token: string }>();

  // --- resolve token ---
  const [resolveToken, { loading, error }] = useMutation(RESOLVE_TOKEN);
  const [resolvedData, setResolvedData] = useState<ResolvedData | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // --- submission ---
  const [submitted, setSubmitted] = useState(false);
  const [completeUpdate] = useMutation(COMPLETE_UPDATE);

  // --- save progress ---
  const [saveProgress] = useMutation(SAVE_PROGRESS);

  // Keep a ref to the debounced save so it doesn't get recreated on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((fields: Record<string, unknown>) => {
      if (!token) return;
      saveProgress({ variables: { token, fields: JSON.stringify(fields) } }).catch(err =>
        console.error('Auto-save failed', err)
      );
    }, 2000),
    [token, saveProgress]
  );

  const sdkInitRef = useRef(false);
  const iframeId = 'opnform-portfolio-update';

  // Resolve the token on mount
  useEffect(() => {
    if (!token) return;

    resolveToken({ variables: { token } })
      .then(result => {
        if (result.data?.resolvePortfolioUpdateToken) {
          const data = result.data.resolvePortfolioUpdateToken;
          setResolvedData({
            ...data,
            savedFields: data.savedFields ? JSON.parse(data.savedFields) : null,
          });
        } else {
          setResolveError('Invalid or expired link. Please request a new portfolio update link.');
        }
      })
      .catch(() => {
        setResolveError('Invalid or expired link. Please request a new portfolio update link.');
      });
  }, [token, resolveToken]);

  // Load the OpnForm SDK and wire up events once the iframe is rendered
  useEffect(() => {
    if (!resolvedData || sdkInitRef.current) return;
    sdkInitRef.current = true;

    const script = document.createElement('script');
    script.src = 'https://forms.angellistindia.com/widgets/opnform-sdk.min.js';
    script.async = true;

    script.onload = () => {
      const sdk = (window as any).opnform;
      if (!sdk) {
        console.error('OpnForm SDK failed to initialise');
        return;
      }

      sdk.on('ready', () => {
        const form = sdk.get(resolvedData.formSlug);
        if (!form) return;

        const prefill: Record<string, unknown> = {
          request_id: resolvedData.requestId,
          deal_id: resolvedData.dealId,
          company_name: resolvedData.companyName,
          ...(resolvedData.savedFields ?? {}),
        };
        form.setFields(prefill);
      });

      sdk.on('dataChange', (event: { data: Record<string, unknown> }) => {
        debouncedSave(event.data);
      });

      sdk.on('submit', async (event: { data: Record<string, unknown> }) => {
        try {
          await completeUpdate({
            variables: { token, submittedFields: JSON.stringify(event.data) },
          });
          setSubmitted(true);
        } catch (err) {
          console.error('Submission failed', err);
        }
      });
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [resolvedData, token, completeUpdate, debouncedSave]);

  // --- Render: loading ---
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50">
        <img className="mx-auto h-12 w-auto" src={alIndiaLogo} alt="AngelList India" />
        <div className="mt-6 flex items-center gap-2 text-gray-500">
          <svg
            className="inline h-5 w-5 animate-spin text-indigo-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#E5E7EB"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentColor"
            />
          </svg>
          <span className="text-sm">Loading your portfolio update form...</span>
        </div>
      </div>
    );
  }

  // --- Render: error ---
  if (resolveError || error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <img className="mx-auto h-12 w-auto" src={alIndiaLogo} alt="AngelList India" />
        <div className="mt-6 w-full max-w-md rounded-lg bg-white p-8 shadow">
          <h2 className="text-center text-xl font-semibold text-gray-900">
            Unable to load portfolio update
          </h2>
          <p className="mt-3 text-center text-sm text-gray-600">
            {resolveError || 'Something went wrong. Please try again later or contact support.'}
          </p>
          <p className="mt-4 text-center text-xs text-gray-400">
            If the issue persists, contact us at{' '}
            <a href="mailto:hi@angellistindia.com" className="text-indigo-600 hover:underline">
              hi@angellistindia.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // --- Render: success ---
  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <img className="mx-auto h-12 w-auto" src={alIndiaLogo} alt="AngelList India" />
        <div className="mt-6 w-full max-w-md rounded-lg bg-white p-8 shadow text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Thank you!</h2>
          <p className="mt-2 text-sm text-gray-600">
            Your portfolio update for{' '}
            <span className="font-medium">{resolvedData?.companyName}</span> has been submitted
            successfully.
          </p>
          <p className="mt-4 text-xs text-gray-400">You can close this window now.</p>
        </div>
      </div>
    );
  }

  // --- Render: form ---
  if (!resolvedData) return null;

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <img className="h-10 w-auto" src={alIndiaLogo} alt="AngelList India" />
        </div>
      </header>

      {/* Main content */}
      <main className="flex flex-1 flex-col items-center px-4 py-8 sm:px-6 lg:px-8">
        <div className="w-full max-w-3xl">
          <h1 className="text-2xl font-bold text-gray-900">
            Portfolio Update for {resolvedData.companyName}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Please fill in the details below. Your progress is saved automatically.
          </p>

          {/* OpnForm iframe — pre-fill via URL params */}
          <iframe
            id={iframeId}
            src={`https://forms.angellistindia.com/forms/${
              resolvedData.formSlug
            }?request_id=${encodeURIComponent(
              resolvedData.requestId
            )}&company_name=${encodeURIComponent(resolvedData.companyName)}`}
            className="mt-6 w-full overflow-hidden rounded-lg bg-white shadow"
            style={{ border: 'none', width: '100%', height: '80vh' }}
          />
        </div>
      </main>
    </div>
  );
}
